'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

interface AnimatedLogoTextProps {
  displaySubtext: boolean;
  animate?: boolean;
}

export default function AnimatedLogoText({ displaySubtext = true, animate = true }: AnimatedLogoTextProps) {
  const [animating, setAnimating] = useState(false);
  const [metallicShimmer, setMetallicShimmer] = useState(false);
  
  useEffect(() => {
    if (!animate) return;
    // Start animation with a long initial delay
    const initialDelay = setTimeout(() => {
      setAnimating(true);
      
      // Reset animation after it completes (8s animation + random pause)
      const interval = setInterval(() => {
        setAnimating(false);
        
        // Slight delay before next animation
        setTimeout(() => setAnimating(true), 300);
      }, Math.random() * 15000); // Total cycle time
      
      return () => clearInterval(interval);
    }, 3000);
    
    return () => clearTimeout(initialDelay);
  }, [animate]);

  // Metallic shimmer effect that triggers occasionally
  useEffect(() => {
    if (!animate) return;
    
    const triggerMetallicShimmer = () => {
      // Random delays between 5-20 seconds
      const delay = 5000 + Math.random() * 15000;
      
      setTimeout(() => {
        if (Math.random() > 0.3) { // 70% chance to trigger
          setMetallicShimmer(true);
          
          // Reset metallic shimmer after animation completes
          setTimeout(() => {
            setMetallicShimmer(false);
            triggerMetallicShimmer(); // Schedule next shimmer
          }, 1500); // Animation duration
        } else {
          triggerMetallicShimmer(); // Try again if not triggered
        }
      }, delay);
    };
    
    triggerMetallicShimmer();
    
    return () => {
      // Cleanup handled by the timeouts themselves
    };
  }, []);

  return (
    <div className="flex flex-col">
      <div className="relative">
        <Link href="/" className={`text-[1.1rem] logo-text font-bold ${animating ? 'shimmer-logo' : 'text-sky-500'}`}>
          Chatlanga
        </Link>
        {metallicShimmer && (
          <div className="metallic-shimmer-overlay absolute inset-0 w-full pointer-events-none" />
        )}
      </div>
      {displaySubtext && <div className="text-[0.6rem] text-gray-500 pl-0.5">Chat to Learn!</div>}
      
      <style jsx global>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
            color: #0ea5e9; /* sky-500 */
          }
          40% {
            color: #2563eb; /* blue-600 */
          }
          60% {
            color: #4f46e5; /* indigo-600 */
          }
          80% {
            color: #2563eb; /* blue-600 */
          }
          100% {
            background-position: -200% 0;
            color: #0ea5e9; /* sky-500 */
          }
        }
        
        .shimmer-logo {
          background: linear-gradient(to right, #0ea5e9, #2563eb, #4f46e5, #2563eb, #0ea5e9);
          background-size: 200% auto;
          color: transparent;
          -webkit-background-clip: text;
          background-clip: text;
          animation: shimmer 8s ease-in-out forwards;
        }

        .dark .shimmer-logo {
          display: none;
        }

        @keyframes metallic-sweep {
          0% {
            transform: translateX(-100%);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          60% {
            opacity: 0.8;
          }
          100% {
            transform: translateX(100%);
            opacity: 0;
          }
        }
        
        .metallic-shimmer-overlay {
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0) 10%, 
            rgba(255, 255, 255, 0.8) 50%, 
            rgba(255, 255, 255, 0) 90%, 
            transparent 100%);
          transform: skewX(-20deg);
          animation: metallic-sweep 1.2s ease-in-out forwards;
          mix-blend-mode: overlay;
          z-index: 10;
        }

        .dark .metallic-shimmer-overlay {
          display: none;
        }
      `}</style>
    </div>
  );
} 